import { Button } from "@/components/ui/button";
import { ArrowRight, Clock } from "lucide-react";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { storeStrategyForRedirect, storeRedirectUrl } from "@/utils/strategySessionUtils";

interface StrategyCardActionProps {
  isPro: boolean;
  hasActiveSubscription: boolean | null;
  onButtonClick: (e: React.MouseEvent) => void;
  label?: string;
  isComingSoon?: boolean;
  strategyData?: any; // Optional strategy data for auth redirects
}

const StrategyCardAction = ({ 
  isPro, 
  hasActiveSubscription, 
  onButtonClick,
  label,
  isComingSoon = false,
  strategyData
}: StrategyCardActionProps) => {
  const navigate = useNavigate();
  const [isPhemexLanding, setIsPhemexLanding] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  
  // Check if user is authenticated
  useEffect(() => {
    const checkAuthStatus = async () => {
      const { data } = await supabase.auth.getSession();
      setIsAuthenticated(!!data.session);
    };
    
    checkAuthStatus();
  }, []);
  
  // Check if we're on the Phemex landing page
  useEffect(() => {
    const checkIfPhemexLanding = () => {
      const isPhemex = window.location.pathname.includes('/landing/phemex');
      setIsPhemexLanding(isPhemex);
    };
    
    checkIfPhemexLanding();
  }, []);
  
  // Determine the button label
  const buttonLabel = isComingSoon 
    ? "Coming Soon" 
    : (label || (isPro && hasActiveSubscription ? "Follow Now" : "Start Following Now"));
  
  // Handle authentication and redirection
  const handleButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Don't do anything if coming soon
    if (isComingSoon) return;

    // If on Phemex landing and not authenticated, redirect to signup
    if (isPhemexLanding && !isAuthenticated) {
      e.preventDefault();
      
      // Store strategy data if provided
      if (strategyData) {
        storeStrategyForRedirect(strategyData);
        
        // Determine where to redirect after signup
        const redirectPath = isPro 
          ? (hasActiveSubscription ? '/follow-strategy-premium-existing' : '/follow-strategy')
          : '/follow-strategy-free';
          
        storeRedirectUrl(redirectPath);
      }
      
      // Navigate to signup
      navigate('/signup', { state: { fromPhemex: true } });
      return;
    }
    
    // Otherwise, use the original click handler
    onButtonClick(e);
  };
  
  return (
    <div className={`p-4 pt-0 ${isPhemexLanding ? 'bg-gray-900' : 'bg-white'} ${isComingSoon ? 'blur-[1px]' : ''}`}>
      <Button 
        className={`w-full h-12 text-base font-medium transition-colors duration-200 rounded-lg ${
          isComingSoon
            ? "bg-gray-400 text-white cursor-not-allowed"
            : isPro
              ? "bg-[#F6C528] text-[#012846] hover:bg-[#F7D44C]" 
              : isPhemexLanding 
                ? "bg-blue-600 text-white hover:bg-blue-700"
                : "bg-[#012846] text-white hover:bg-[#407096]"
        }`}
        onClick={handleButtonClick}
        disabled={isComingSoon}
      >
        {isComingSoon ? (
          <>
            <Clock className="w-4 h-4 mr-2" />
            {buttonLabel}
          </>
        ) : (
          <>
            {buttonLabel}
            <ArrowRight className="w-4 h-4 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default StrategyCardAction;
